import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { theme } from '~/theme';

import AddIcon from '@mui/icons-material/Add';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridEditInputCell,
  GridExpandMoreIcon,
  GridRenderEditCellParams,
  GridRowClassNameParams,
  GridRowId,
  GridRowModes,
  GridRowModesModel,
  useGridApiContext,
} from '@mui/x-data-grid';

import { definitions } from '@luxuryescapes/contract-svc-promo';

import { DEFAULT_PAGE_SIZES } from '~/consts/filters';

import useQuery from '~/hooks/useQuery';

import {
  InternalPromoWithDiscounts,
  PromoCodeConditionMutationPayload,
  PromoCondition,
  PromoConditionRaw,
  PromoConditionTypeConfig,
  createOrUpdatePromoCondition,
  deletePromoCodeCondition,
} from '~/services/PromoService';
import { parseDayjs } from '~/services/TimeService';

import DateFormatterDetailed from '~/utils/dateFormatterDetails';
import { noop } from '~/utils/noop';

import ConditionValueDatetimeField, {
  formatIsoDatetimeForConditionDisplay,
  getConditionDatetimeApplyHighlightColor,
} from '../Common/Elements/ConditionValueDatetimeField';
import PageSubheader from '../Common/Elements/PageSubheader';
import HeaderInfo from '../Common/HeaderInfo';
import Spinner from '../Common/Spinner';
import DebugModal from '../DebugModal/DebugModal';
import MultiDiscountSelect, { renderMultiDiscountValue } from '../PromoV3/MultiDiscountSelect';
import { formatConditionTypeOptionLabel, sortConditionTypeOptions } from '../Vouchers/voucherConditionFormatters';

import PlaceIdCoordinateLookupHelper, { PLACE_ID_CONDITION_KEY } from './PlaceIdCoordinateLookupHelper';
import PromoConditionTypeSelect from './PromoConditionTypeSelect';
import { formatDateOnlyConditionValue } from './conditionDateFormatter';
import PromoCodeUser from './formatters/PromoCodeUser';
import { PendingCopiedPromoCondition } from './promoConditionCopyUtils';
import { getValidatedConditionApplyType } from './promoConditionTypeUtils';

function PromoConditionValueEditCell({
  params,
  promoCodeConditionTypeConfig,
  conditions,
}: {
  params: GridRenderEditCellParams<PromoCondition, string>;
  promoCodeConditionTypeConfig: Array<PromoConditionTypeConfig>;
  conditions: Array<PromoCondition>;
}) {
  const apiRef = useGridApiContext();
  const currentRow =
    conditions.find(
      (c) => c.id === params.row.id || c.id_promo_code_condition === params.row.id_promo_code_condition,
    ) ?? params.row;
  const config = currentRow.config ?? promoCodeConditionTypeConfig.find((ct) => ct.key === currentRow.condition_type);
  const isDatetime = config?.conditionValueType === 'datetime';

  if (!isDatetime) {
    const displayValue = typeof params.value === 'string' ? formatDateOnlyConditionValue(params.value) : params.value;

    return <GridEditInputCell {...params} value={displayValue} required />;
  }

  const raw =
    (typeof params.value === 'string' ? params.value : String(params.value ?? '')) ||
    (typeof currentRow.condition_value === 'string' ? currentRow.condition_value : '');

  return (
    <ConditionValueDatetimeField
      value={raw}
      onCommit={(iso) => {
        void apiRef.current.setEditCellValue({ id: params.id, field: 'condition_value', value: iso });
      }}
    />
  );
}

function ConditionApplyTypeSelect({
  value,
  options,
  id,
  field,
}: {
  value: string;
  options: Array<string>;
  id: string;
  field: string;
}) {
  const apiRef = useGridApiContext();
  const initialSelectValue = options.length > 0 && (!value || !options.includes(value)) ? options[0] : value || '';
  const [currentValue, setCurrentValue] = useState(initialSelectValue);

  useEffect(() => {
    const newValue = value || '';
    if (options.length === 0) {
      setCurrentValue('');
      return;
    }
    if (!newValue || !options.includes(newValue)) {
      const resetValue = options[0];
      setCurrentValue(resetValue);
      apiRef.current.setEditCellValue({ id, field, value: resetValue });
      return;
    }
    setCurrentValue(newValue);
  }, [value, options, id, field, apiRef]);

  const handleChange = (event: any) => {
    const newValue = event.target.value;
    setCurrentValue(newValue);
    apiRef.current.setEditCellValue({ id, field, value: newValue });
  };

  return (
    <Select value={currentValue} onChange={handleChange} fullWidth size="small">
      {options.map((type) => (
        <MenuItem key={type} value={type}>
          {type}
        </MenuItem>
      ))}
    </Select>
  );
}

interface Props {
  promoCode: InternalPromoWithDiscounts;
  promoCodeConditionTypeConfig: Array<PromoConditionTypeConfig>;
  onConditionRowSelect: (condition: PromoCondition | null) => void;
  highlightedConditionIds?: Array<string>;
  onConditionUpdate?: () => void;
  pendingCopiedConditions?: Array<PendingCopiedPromoCondition>;
  onPendingCopiedConditionsChange?: React.Dispatch<React.SetStateAction<Array<PendingCopiedPromoCondition>>>;
}

const addUniqueID = (
  condition: PromoConditionRaw,
  promoConditionTypes: Array<PromoConditionTypeConfig>,
): PromoCondition => {
  const existing = 'id_promo_code_condition' in condition;
  return {
    ...condition,
    id: condition.id_promo_code_condition ?? uuid(),
    isNew: !existing,
    config: promoConditionTypes.find((ct) => ct.key === condition.condition_type),
  };
};

function PromoConditions({
  promoCode,
  promoCodeConditionTypeConfig,
  onConditionRowSelect,
  highlightedConditionIds,
  onConditionUpdate,
  pendingCopiedConditions = [],
  onPendingCopiedConditionsChange,
}: Props) {
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [loadingRows, setLoadingRows] = useState<Set<string>>(new Set());
  const [conditionTypeHelpExpanded, setConditionTypeHelpExpanded] = useState<Record<string, boolean>>({});
  const { enqueueSnackbar } = useSnackbar();
  const brand = useSelector((state: App.State) => state.tenant.brand);
  const query = useQuery();
  const isDev = !!query.get('dev');

  const [conditions, setConditions] = useState<Array<PromoCondition>>(
    promoCode.promo_conditions.map((condition) => addUniqueID(condition, promoCodeConditionTypeConfig)) ?? [],
  );
  const allConditions = useMemo(
    () => [...conditions, ...pendingCopiedConditions],
    [conditions, pendingCopiedConditions],
  );

  useEffect(() => {
    setConditions(promoCode.promo_conditions.map((condition) => addUniqueID(condition, promoCodeConditionTypeConfig)));
  }, [promoCode.promo_conditions, promoCodeConditionTypeConfig]);
  /**
   * Parent `conditions` can lag the grid during row edit; keep the place-ID helper visible after it was shown
   * (e.g. failed save) — matches VoucherConditions pattern.
   */
  const [placeIdTypeSelectedInEdit, setPlaceIdTypeSelectedInEdit] = useState(false);

  const getRowClassName = (params: GridRowClassNameParams<PromoCondition>): string => {
    if (highlightedConditionIds?.includes(params.row.id_promo_code_condition ?? params.row.id)) {
      return 'highlighted-row';
    }
    return '';
  };

  const handleAddCondition = () => {
    const defaultConditionType = promoCodeConditionTypeConfig[0]?.key;
    const defaultConditionConfig = promoCodeConditionTypeConfig.find((ct) => ct.key === defaultConditionType);
    const defaultConditionApplyType = defaultConditionConfig?.conditionApplyTypes?.[0];
    const newCondition = addUniqueID(
      {
        id_promo_code: promoCode?.id_promo_code,
        condition_type: defaultConditionType,
        condition_apply_type: defaultConditionApplyType,
        isNew: true,
        brand: brand,
      } as unknown as PromoConditionRaw,
      promoCodeConditionTypeConfig,
    );
    setConditions([...conditions, newCondition]);

    setRowModesModel((oldModel) => ({
      ...oldModel,
      [newCondition.id]: { mode: GridRowModes.Edit, fieldToFocus: 'region' },
    }));
  };

  const handleCancelCondition = useCallback(
    (params: PromoCondition) => {
      setPlaceIdTypeSelectedInEdit(false);
      if ((params as PendingCopiedPromoCondition).isPendingCopy) {
        onPendingCopiedConditionsChange?.((prev) => prev.filter((row) => row.id !== params.id));
        onConditionRowSelect(null);
        return;
      }
      if (params?.isNew) {
        setConditions(
          conditions.filter(
            (row) => row.id !== params.id && row.id_promo_code_condition !== params.id_promo_code_condition,
          ),
        );
      } else {
        setRowModesModel({
          ...rowModesModel,
          [params.id_promo_code_condition]: { mode: GridRowModes.View, ignoreModifications: true },
        });
      }
      onConditionRowSelect(null);
    },
    [onConditionRowSelect, conditions, rowModesModel, onPendingCopiedConditionsChange],
  );

  const handleEditCondition = useCallback(
    (params: PromoCondition) => {
      if (params.condition_type === PLACE_ID_CONDITION_KEY) {
        setPlaceIdTypeSelectedInEdit(true);
      }
      setRowModesModel({
        ...rowModesModel,
        [params?.id ?? params?.id_promo_code_condition]: { mode: GridRowModes.Edit, fieldToFocus: 'region' },
      });
      onConditionRowSelect(params);
    },
    [promoCodeConditionTypeConfig, rowModesModel],
  );

  const handleDeleteCondition = async (params: PromoCondition) => {
    if ((params as PendingCopiedPromoCondition).isPendingCopy) {
      onPendingCopiedConditionsChange?.((prev) => prev.filter((row) => row.id !== params.id));
      onConditionRowSelect(null);
      return;
    }
    if (confirm('are you sure you want to delete this condition?')) {
      try {
        await deletePromoCodeCondition(params.id_promo_code_condition);
        enqueueSnackbar('Condition deleted successfully');
        setConditions(conditions.filter((row) => row.id_promo_code_condition !== params.id_promo_code_condition));
        if (onConditionUpdate) {
          onConditionUpdate();
        }
      } catch (err) {
        enqueueSnackbar(err.message, { variant: 'error' });
      }
      onConditionRowSelect(null);
    }
  };

  const handleSaveCondition = async (params: PromoCondition) => {
    setRowModesModel({
      ...rowModesModel,
      [params?.id ?? params.id_promo_code_condition]: { mode: GridRowModes.View },
    });
    onConditionRowSelect(null);
  };

  // Function to update config when condition_type changes (only for new rows)
  const updateConditionConfig = (rowId: string, newConditionType: string) => {
    setPlaceIdTypeSelectedInEdit(newConditionType === PLACE_ID_CONDITION_KEY);
    const newConfig = promoCodeConditionTypeConfig.find((ct) => ct.key === newConditionType);
    const updateCondition = (condition: PromoCondition) => {
      if (condition.id !== rowId) {
        return condition;
      }
      const updatedCondition = {
        ...condition,
        condition_type: newConditionType as definitions['PromoCodeConditionPresented']['condition_type'],
        config: newConfig,
      };
      // Reset condition_apply_type when condition_type changes for new rows
      const validatedApplyType = getValidatedConditionApplyType(
        newConditionType,
        condition.condition_apply_type,
        promoCodeConditionTypeConfig,
        true, // alwaysReset = true when condition_type changes
      );
      updatedCondition.condition_apply_type =
        validatedApplyType as definitions['PromoCodeConditionPresented']['condition_apply_type'];
      updatedCondition.condition_value = '';
      return updatedCondition;
    };

    setConditions((prevConditions) => prevConditions.map(updateCondition));
    onPendingCopiedConditionsChange?.((prevConditions) =>
      prevConditions.map((condition) => updateCondition(condition) as PendingCopiedPromoCondition),
    );
  };

  /** Keep React `conditions` aligned with the row the user tried to save when persistence fails. */
  const mergeFailedSaveIntoConditions = useCallback(
    (failedRowId: GridRowId, failedRow: PromoCondition) => {
      const mergeCondition = (condition: PromoCondition) => {
        const key = condition.id_promo_code_condition ?? condition.id;
        if (String(key) !== String(failedRowId)) {
          return condition;
        }
        const config = promoCodeConditionTypeConfig.find((ct) => ct.key === failedRow.condition_type);
        const validatedApplyType = getValidatedConditionApplyType(
          failedRow.condition_type,
          failedRow.condition_apply_type,
          promoCodeConditionTypeConfig,
          false,
        );
        return {
          ...condition,
          condition_type: failedRow.condition_type,
          condition_value: failedRow.condition_value,
          condition_apply_type:
            validatedApplyType as definitions['PromoCodeConditionPresented']['condition_apply_type'],
          customized_message: failedRow.customized_message,
          promo_code_discount_ids: failedRow.promo_code_discount_ids ?? [],
          config: config ?? condition.config,
        };
      };

      setConditions((prev) => prev.map(mergeCondition));
      onPendingCopiedConditionsChange?.((prev) =>
        prev.map((condition) => mergeCondition(condition) as PendingCopiedPromoCondition),
      );
      if (failedRow.condition_type === PLACE_ID_CONDITION_KEY) {
        setPlaceIdTypeSelectedInEdit(true);
      }
    },
    [onPendingCopiedConditionsChange, promoCodeConditionTypeConfig],
  );

  const processRowUpdate = async (row: PromoCondition) => {
    const rowId = row.id_promo_code_condition ?? row.id;

    // Set loading state
    setLoadingRows((prev) => new Set(prev).add(rowId));

    try {
      // Validate condition_apply_type before save (non-destructive - only fixes invalid values)
      // Since condition_type can't be changed for existing rows, we just validate the apply type
      const validatedApplyType = getValidatedConditionApplyType(
        row.condition_type,
        row.condition_apply_type,
        promoCodeConditionTypeConfig,
        false, // alwaysReset = false for validation before save (non-destructive)
      );

      if ((row as PendingCopiedPromoCondition).isPendingCopy) {
        const updatedCopiedCondition = {
          ...row,
          isNew: true,
          isPendingCopy: true,
          condition_apply_type:
            validatedApplyType as definitions['PromoCodeConditionPresented']['condition_apply_type'],
          config: row.config ?? promoCodeConditionTypeConfig.find((ct) => ct.key === row.condition_type),
        } as PendingCopiedPromoCondition;

        onPendingCopiedConditionsChange?.((prev) =>
          prev.map((condition) => (condition.id === row.id ? updatedCopiedCondition : condition)),
        );

        setLoadingRows((prev) => {
          const next = new Set(prev);
          next.delete(rowId);
          return next;
        });

        return updatedCopiedCondition;
      }

      const payload: PromoCodeConditionMutationPayload & {
        promoCodeCondition: { condition_apply_type: string };
      } = {
        promoCodeCondition: {
          id_promo_code_condition: row.id_promo_code_condition,
          condition_type: row.condition_type,
          condition_value: row.condition_value,
          condition_apply_type:
            validatedApplyType as definitions['PromoCodeConditionPresented']['condition_apply_type'],
          id_promo_code: row.id_promo_code,
          customized_message: row.customized_message === '' ? undefined : (row.customized_message ?? undefined),
          promo_code_discount_ids: row.promo_code_discount_ids ?? [],
        },
      };

      const result = await createOrUpdatePromoCondition(payload);
      if (result.status !== 200) {
        const error = new Error(JSON.stringify(result.result));
        enqueueSnackbar(JSON.stringify(result.result), {
          autoHideDuration: 5000,
        });
        // Keep the row in edit mode by setting rowModesModel and throwing the error
        setRowModesModel((prevModel) => ({
          ...prevModel,
          [rowId]: { mode: GridRowModes.Edit },
        }));

        // Clear loading state
        setLoadingRows((prev) => {
          const next = new Set(prev);
          next.delete(rowId);
          return next;
        });

        throw error;
      } else {
        const newCondition = result.result.promoCodeCondition;
        const updatedCondition = addUniqueID(newCondition, promoCodeConditionTypeConfig);

        // Ensure the returned condition has the validated condition_apply_type
        updatedCondition.condition_apply_type =
          validatedApplyType as definitions['PromoCodeConditionPresented']['condition_apply_type'];

        const newConditionArray = conditions.map((c) => {
          if (c.id_promo_code_condition === newCondition.id_promo_code_condition) {
            return updatedCondition;
          } else if (c.id === row.id) {
            return updatedCondition;
          }
          return c;
        });

        setConditions(newConditionArray);

        if (onConditionUpdate) {
          onConditionUpdate();
        }

        // Clear loading state
        setLoadingRows((prev) => {
          const next = new Set(prev);
          next.delete(rowId);
          return next;
        });

        return updatedCondition;
      }
    } catch (err) {
      mergeFailedSaveIntoConditions(rowId, row);
      console.error(
        `processConditionRowUpdate - ${row?.id ?? row.id_promo_code_condition} - error\n${err.message}\n${
          err.stack
        }\n\n ${JSON.stringify(row)}`,
      );
      enqueueSnackbar(JSON.stringify(err), {
        autoHideDuration: 5000,
      });
      // Keep the row in edit mode by setting rowModesModel and re-throwing the error
      setRowModesModel((prevModel) => ({
        ...prevModel,
        [rowId]: { mode: GridRowModes.Edit },
      }));

      // Clear loading state
      setLoadingRows((prev) => {
        const next = new Set(prev);
        next.delete(rowId);
        return next;
      });

      throw err;
    }
  };

  const handleRowClick = (params: PromoCondition) => {
    if (params.condition_type === PLACE_ID_CONDITION_KEY) {
      setPlaceIdTypeSelectedInEdit(true);
    }
    onConditionRowSelect(params);
  };

  const hasPlaceIdCondition = allConditions.some((c) => c.condition_type === PLACE_ID_CONDITION_KEY);
  const showPlaceIdHelper = hasPlaceIdCondition || placeIdTypeSelectedInEdit;

  const sharedOpts: Partial<GridColDef<PromoCondition>> = {
    editable: true,
    sortable: true,
    filterable: false,
    hideable: false,
    disableColumnMenu: true,
    flex: 1,
  };

  const isNew = (params: PromoCondition | undefined) => {
    if (!params) {
      return false;
    }
    return !('id_promo_code_condition' in params);
  };

  const getColumns = (isCreate = false): Array<GridColDef<PromoCondition>> => [
    {
      ...sharedOpts,
      field: 'condition_type',
      headerName: 'Condition Type',
      type: 'singleSelect',
      valueOptions:
        promoCodeConditionTypeConfig
          .map((ct) => {
            return {
              value: ct.key,
              label: formatConditionTypeOptionLabel(ct.key, ct),
              category: ct.category,
              isBeingDeprecated: ct.isBeingDeprecated,
            };
          })
          .sort(sortConditionTypeOptions) ?? [],
      renderCell: (params) => {
        if (!params.row.config) {
          return <Typography>{params.row.condition_type} (config missing)</Typography>;
        }
        const rowId = String(params.row.id);
        const expanded = conditionTypeHelpExpanded[rowId] ?? false;
        return (
          <Accordion
            sx={{ width: '100%' }}
            expanded={expanded}
            onChange={(_, nextExpanded) =>
              setConditionTypeHelpExpanded((prev) => ({
                ...prev,
                [rowId]: nextExpanded,
              }))
            }
          >
            <AccordionSummary expandIcon={<GridExpandMoreIcon />} aria-controls="panel-content" id="panel-header">
              <Stack spacing={0.5}>
                <Typography
                  sx={{
                    textDecoration: params.row.config.isBeingDeprecated ? 'line-through' : 'none',
                  }}
                >
                  {formatConditionTypeOptionLabel(params.row.config.key, params.row.config)}
                </Typography>
                {params.row.config.isBeingDeprecated && (
                  <Alert severity="warning">This condition type is deprecated.</Alert>
                )}
                {!params.row.config.isAvailableInAPP && (
                  <Typography variant="caption" color="warning.main">
                    [Behavior blocked in APP]
                  </Typography>
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={0.5}>
                {params.row.config.isBeingDeprecated ? (
                  <Alert severity="warning">This condition type is deprecated.</Alert>
                ) : null}
                <Typography variant="body2">{params.row.config.longDescription}</Typography>
                {params.row.config.conditionValueType ? (
                  <Typography variant="caption" color="text.secondary">
                    Value type: {params.row.config.conditionValueType}
                  </Typography>
                ) : null}
                {params.row.config.conditionApplyTypes?.length ? (
                  <Typography variant="caption" color="text.secondary">
                    Apply types: {params.row.config.conditionApplyTypes.join(', ')}
                  </Typography>
                ) : null}
                {params.row.config.suggestedConditionValues?.length ? (
                  <Typography variant="caption" color="text.secondary">
                    Suggested values: {params.row.config.suggestedConditionValues.join(', ')}
                  </Typography>
                ) : null}
                {params.row.config.canUseDiscountFilterIds ? (
                  <Typography variant="caption" color="text.secondary">
                    Supports discount filters
                  </Typography>
                ) : null}
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      },
      renderEditCell: (params) => {
        if (!params.row.isNew) {
          return (
            <Stack spacing={1} width="100%">
              <Typography
                sx={{
                  textDecoration: params.row.config?.isBeingDeprecated ? 'line-through' : 'none',
                }}
              >
                {params.row.config?.key
                  ? formatConditionTypeOptionLabel(params.row.config.key, params.row.config)
                  : params.row.config?.name}
              </Typography>
              {!params.row.config?.isAvailableInAPP && (
                <Typography variant="caption" color="warning.main">
                  [Behavior blocked in APP]
                </Typography>
              )}
              {params.row.config?.isBeingDeprecated && (
                <Typography variant="caption" color="warning.main">
                  [This condition type is deprecated and will not be a selectable option soon]
                </Typography>
              )}
            </Stack>
          );
        }

        return (
          <PromoConditionTypeSelect
            params={params}
            promoCodeConditionTypeConfig={promoCodeConditionTypeConfig}
            onConditionTypeChange={updateConditionConfig}
          />
        );
      },
      preProcessEditCellProps: (params) => {
        // The ConditionTypeSelect component handles the update, but we still need to process the props
        return params.props;
      },
      headerAlign: 'center',
      align: 'center',
      display: 'flex',
      flex: 1,
      minWidth: 150,
    },
    {
      ...sharedOpts,
      field: 'condition_apply_type',
      headerName: 'Condition Apply Type',
      renderCell: (params) => {
        if (params.row.condition_apply_type === 'NONE') {
          return <Typography>--</Typography>;
        }
        return <Typography>{params.row.condition_apply_type}</Typography>;
      },
      renderEditCell: (params) => {
        // Get the latest row data from conditions state to ensure we have the updated config and value
        const currentRow = allConditions.find(
          (c) => c.id === params.row.id || c.id_promo_code_condition === params.row.id_promo_code_condition,
        );
        const row = currentRow || params.row;

        // Try to get config from the row, or load it if missing
        let config = row.config;
        if (!config && row.condition_type) {
          config = promoCodeConditionTypeConfig.find((ct) => ct.key === row.condition_type);
          // Update the row with the config if we found it
          if (config) {
            updateConditionConfig(row.id, row.condition_type);
          }
        }

        const conditionApplyTypes = config?.conditionApplyTypes;
        if (!row.condition_type) {
          return (
            <Tooltip title="Please select a condition type first">
              <Typography>Select condition type first</Typography>
            </Tooltip>
          );
        }

        if (!conditionApplyTypes || conditionApplyTypes.length === 0) {
          return (
            <Tooltip title="This condition type does not yet support condition apply types, let the marketing team know if you need this functionality for a specific condition type">
              <Typography>--</Typography>
            </Tooltip>
          );
        }

        // Use a custom Select component with proper DataGrid integration
        return (
          <ConditionApplyTypeSelect
            value={row.condition_apply_type || ''}
            options={conditionApplyTypes}
            id={params.id.toString()}
            field="condition_apply_type"
          />
        );
      },
      preProcessEditCellProps: (params) => {
        // Ensure config is loaded when entering edit mode
        if (!params.row.config && params.row.condition_type) {
          updateConditionConfig(params.row.id, params.row.condition_type);
        }
        // Ensure the value is properly passed to the edit cell and matches the row state
        // This ensures that when condition_type changes and condition_apply_type is reset,
        // the cell value is updated accordingly
        const currentRow = allConditions.find(
          (c) => c.id === params.row.id || c.id_promo_code_condition === params.row.id_promo_code_condition,
        );
        const updatedValue = currentRow?.condition_apply_type || params.row.condition_apply_type || '';
        return {
          ...params.props,
          value: updatedValue,
        };
      },
      align: 'center',
      display: 'flex',
    },

    {
      ...sharedOpts,
      field: 'condition_value',
      headerName: 'Condition Value',
      renderCell: (params) => {
        const config =
          params.row.config ?? promoCodeConditionTypeConfig.find((ct) => ct.key === params.row.condition_type);
        const raw =
          typeof params.row.condition_value === 'string'
            ? params.row.condition_value
            : String(params.row.condition_value ?? '');
        if (config?.conditionValueType === 'datetime' && raw) {
          const highlight = getConditionDatetimeApplyHighlightColor(params.row.condition_apply_type, raw);
          return (
            <Typography variant="body2" title={raw} {...(highlight ? { color: highlight } : {})}>
              {formatIsoDatetimeForConditionDisplay(raw)}
            </Typography>
          );
        }
        return <Typography variant="body2">{raw ? formatDateOnlyConditionValue(raw) : '—'}</Typography>;
      },
      renderEditCell: (params) => (
        <PromoConditionValueEditCell
          params={params}
          promoCodeConditionTypeConfig={promoCodeConditionTypeConfig}
          conditions={allConditions}
        />
      ),
      preProcessEditCellProps: (params) => {
        const currentRow =
          allConditions.find(
            (c) => c.id === params.row.id || c.id_promo_code_condition === params.row.id_promo_code_condition,
          ) ?? params.row;
        const config =
          currentRow.config ?? promoCodeConditionTypeConfig.find((ct) => ct.key === currentRow.condition_type);
        const cellValue =
          typeof params.props.value === 'string' ? params.props.value : String(params.props.value ?? '');
        if (config?.conditionValueType === 'datetime') {
          const hasError = !cellValue.trim() || !parseDayjs(cellValue).isValid();
          return { ...params.props, error: hasError };
        }
        const hasError = !cellValue.trim();
        return { ...params.props, error: hasError };
      },
      headerAlign: 'center',
      align: 'center',
      display: 'flex',
    },
    {
      ...sharedOpts,
      field: 'promo_code_discount_ids',
      renderHeader: () => (
        <HeaderInfo
          title="When discounts are specified here, the discounts will only apply to the order only when the associated condition(s) are met. (when set) Click the condition to highlight which discounts will be only applied with that condition. Multiple conditions can be applied to each discount"
          header="Discount Filter"
          iconType="info"
        />
      ),
      renderCell: (params) =>
        renderMultiDiscountValue(params.row.promo_code_discount_ids ?? [], promoCode.promo_discounts),
      renderEditCell: (params) => {
        if (!promoCodeConditionTypeConfig) {
          return <Spinner />;
        }
        if (!params.row.config?.canUseDiscountFilterIds) {
          return null;
        }
        return (
          <MultiDiscountSelect
            id={`${params.id}`}
            value={params.row.promo_code_discount_ids ?? []}
            promoDiscounts={promoCode.promo_discounts}
            multiSelectType="promo_code_discount_ids"
          />
        );
      },
      headerAlign: 'center',
      align: 'center',
      display: 'flex',
    },
    {
      ...sharedOpts,
      field: 'customized_message',
      headerName: 'Customized Msg',
      renderEditCell: (params) => <GridEditInputCell {...params} />,
      renderHeader: () => (
        <HeaderInfo
          title="When a message is specified here, it will override the default error message when this condition fails."
          header="Customized Message"
          iconType="info"
        />
      ),
      headerAlign: 'center',
      align: 'center',
      display: 'flex',
    },
    {
      ...sharedOpts,
      field: 'action',
      headerName: 'Action',
      sortable: false,
      editable: false,
      minWidth: 220,
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const isEditMode = rowModesModel[params.id]?.mode === GridRowModes.Edit;
        const row = params.row;
        const rowId = row.id_promo_code_condition ?? row.id;
        const isLoading = loadingRows.has(rowId);

        if (isEditMode) {
          return (
            <Stack spacing={1} alignItems="flex-start" width="100%">
              <Stack direction="row" spacing={1} alignItems="center">
                {isLoading && <CircularProgress size={16} />}
                <Button variant="contained" size="small" onClick={() => handleSaveCondition(row)} disabled={isLoading}>
                  Save
                </Button>
                <Button variant="outlined" size="small" onClick={() => handleCancelCondition(row)} disabled={isLoading}>
                  Cancel
                </Button>
              </Stack>

              <Stack spacing={2}>
                {row.created_at && (
                  <Typography variant="caption" color="textSecondary">
                    Created: <DateFormatterDetailed date={row.created_at} />
                  </Typography>
                )}
                {row.updated_at && (
                  <Typography variant="caption" color="textSecondary">
                    Updated: <DateFormatterDetailed date={row.updated_at} />
                  </Typography>
                )}
              </Stack>

              <PromoCodeUser userId={row.modified_by} />

              {row.id_promo_code_condition && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => handleDeleteCondition(row)}
                  disabled={isLoading}
                >
                  Delete
                </Button>
              )}
            </Stack>
          );
        }

        return (
          <Stack spacing={1} alignItems="flex-start">
            <Button variant="contained" size="small" onClick={() => handleEditCondition(row)} disabled={isLoading}>
              Edit
            </Button>
          </Stack>
        );
      },
    },
    {
      field: 'debug',
      headerName: 'Info',
      renderCell: (params) => {
        if (!promoCodeConditionTypeConfig) {
          return null;
        }
        const data = {
          data: params.row,
          config: promoCodeConditionTypeConfig.find((ct) => ct.key === params.row.condition_type),
        };
        return <DebugModal type="generic" data={data ?? { data: 'None' }} />;
      },
      ...sharedOpts,
      flex: 0,
    },
  ];

  return (
    <Box mt={3}>
      <PageSubheader title="Rules">
        <Stack direction="row">
          <Typography variant="h5" pr={2}></Typography>
          {isDev && (
            <DebugModal
              type="generic"
              data={{ allConditions: promoCodeConditionTypeConfig, theseConditions: allConditions }}
            />
          )}
          <Button onClick={handleAddCondition} variant="contained" size="small" startIcon={<AddIcon />}>
            Add Condition
          </Button>
        </Stack>
      </PageSubheader>
      {promoCodeConditionTypeConfig ? (
        <>
          <DataGrid
            columns={getColumns(isNew(allConditions[0]))}
            editMode="row"
            autoHeight
            rows={allConditions}
            rowModesModel={rowModesModel}
            initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
            pageSizeOptions={DEFAULT_PAGE_SIZES}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={(error) => {
              enqueueSnackbar(error.message, { autoHideDuration: 5000 });
            }}
            getRowId={(row) => row.id_promo_code_condition ?? row.id}
            onRowEditStop={noop} // Ignore clicks outside the table
            onRowClick={(params) => handleRowClick(params.row)}
            getRowHeight={() => 'auto'}
            disableColumnSorting
            disableRowSelectionOnClick
            getRowClassName={getRowClassName}
            sx={{
              '& .highlighted-row': {
                backgroundColor: theme.palette.primary.dark,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                },
              },
            }}
          />
          {showPlaceIdHelper && (
            <Box sx={{ mt: 2 }}>
              <PlaceIdCoordinateLookupHelper compact />
            </Box>
          )}
        </>
      ) : (
        <Spinner />
      )}
    </Box>
  );
}

export default PromoConditions;
